import { yupResolver } from "@hookform/resolvers/yup";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useGetLoggedInUserDetails } from "../../../../../aAdminTabTeam2/hooksAndFunctions/customHooks/useGetLoggedInUserDetails";
import { useSearchParticipantListMutation } from "../../../../../api/services/bulkInsertActivity/bulkInsertApis";
import { ReactComponent as ResetAllIcon } from "../../../../../assets/images/reset-all.svg";
import {
  AccordionHeader,
  AccordionHeaderText,
  BulkInsertFooter,
  InformationModal,
  MuiBox,
  MuiButtonPrimary,
  MuiTypography,
  WarningConfirmationModal,
} from "../../../../../components";
import { ButtonWithIcon } from "../../../../../components/buttonWithIcon/ButtonWithIcon";
import { BE_ERROR_CODES, PAGE_IDS } from "../../../../../helpers/constants";
import { getDateFormatByCountry } from "../../../../../helpers/constants/countryDateFormat";
import {
  convertToUTCFormat,
  getParsedBEErrors,
  getValueOrBlank,
  getValueOrZero,
  isNotEmpty,
  trimValue,
} from "../../../../../helpers/utils";
import useAccordion from "../../../../../hooks/useAccordion";
import { useSiteCore } from "../../../../../hooks/useSiteCore";
import {
  toggleLoader,
  toggleToast,
} from "../../../../../redux/slices/appState/appStateSlice";
import { bulkActivityListSelector } from "../../../../../redux/slices/program/programSelector";
import {
  setBulkActivityList,
  setBulkActivityProgramTemplateId,
  setBulkActivitySelectedParticipants,
} from "../../../../../redux/slices/program/programSlice";
import { Separator } from "../../../../../styles/common.style";
import {
  SearchParticipantFormKey,
  SearchParticipantResult,
} from "../../../modelsAndSchemas/Models";
import { BulkActivitySearchParticipantSchema } from "../../../modelsAndSchemas/ValidationSchema";
import { bulkActivitySiteCoreKeys } from "../../../siteCore/SiteCoreKeys";
import { BulkActivitySearchParticipantsWrapper } from "./BulkActivitySearchParticipants.style";
import { SearchParticipantsForm } from "./searchParticipantsForm/SearchParticipantsForm";
import { SearchParticipantsResult } from "./searchParticipantsResult/SearchParticipantsResult";

const pageId = PAGE_IDS.CREATE_BULK_ACTIVITY;

const initialAccordionStatus = {
  searchParticipant: true,
  searchResult: true,
  all: true,
};

export interface BulkActivitySearchParticipantsProps {
  onNextButtonClick: () => void;
  currentStep: number;
  handleNavigateBack: () => void;
}

export interface BulkActivitySearchParticipantsRef {
  handleResetButtonRef: () => void;
}

export const BulkActivitySearchParticipants = forwardRef<
  BulkActivitySearchParticipantsRef,
  BulkActivitySearchParticipantsProps
>(({ onNextButtonClick, currentStep, handleNavigateBack }, ref) => {
  const dispatch = useDispatch();
  const activityList = useSelector(bulkActivityListSelector);
  const { commonLabels, fetchComponentTitle, fetchLabel } = useSiteCore();
  const { loggedInUser } = useGetLoggedInUserDetails();
  const userCountryDateFormat = getDateFormatByCountry(
    getValueOrBlank(loggedInUser?.countryCode),
  );

  const programTemplateRef = useRef<{
    resetValues: (values?: string[]) => void;
  }>(null);

  const [showInformationModal, setShowInformationModal] = useState(false);
  const [showResetConfirmationModal, setShowResetConfirmationModal] =
    useState(false);
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [isSearchClicked, setIsSearchClicked] = useState(false);
  const [participantResultsList, setParticipantResultsList] = useState<
    SearchParticipantResult[]
  >([]);

  const { accordionStatus, handleIndividualAccordionChange } = useAccordion(
    initialAccordionStatus,
  );

  const {
    mutateAsync: getParticipantList,
    isPending: isParticipantPending,
    isError: isParticipantError,
    data: participantResults,
    error: participantError,
  } = useSearchParticipantListMutation();

  const defaultFilter = bulkActivitySiteCoreKeys.filterByContains;

  const searchParticipantsDefaultValues = {
    [SearchParticipantFormKey.participantName]: "",
    [SearchParticipantFormKey.participantNameFilter]: defaultFilter,
    [SearchParticipantFormKey.programName]: "",
    [SearchParticipantFormKey.programNameFilter]: defaultFilter,
    [SearchParticipantFormKey.clientName]: "",
    [SearchParticipantFormKey.clientNameFilter]: defaultFilter,
    [SearchParticipantFormKey.productLine]: "",
    [SearchParticipantFormKey.programTemplate]: [],
    [SearchParticipantFormKey.status]: "",
    [SearchParticipantFormKey.deliveryCountry]: "",
    [SearchParticipantFormKey.deliveryOffice]: "",
    [SearchParticipantFormKey.deliveryOfficeFilter]: defaultFilter,
    [SearchParticipantFormKey.coach]: "",
    [SearchParticipantFormKey.coachFilter]: defaultFilter,
    [SearchParticipantFormKey.createdDate]: "",
  };

  useEffect(() => {
    if (isNotEmpty(participantResults)) {
      setParticipantResultsList(participantResults?.data ?? []);
    } else {
      setParticipantResultsList([]);
    }
  }, [participantResults]);

  useEffect(() => {
    if (isParticipantError) {
      dispatch(toggleLoader({ isVisible: false }));
      const error = getParsedBEErrors(participantError);
      if (
        isNotEmpty(error) &&
        error[0].errorCode === BE_ERROR_CODES.SEARCH_PROGRAM_LIMIT_EXCEEDED
      ) {
        setShowErrorMessage(true);
        setIsSearchClicked(false);
      }
    }
  }, [isParticipantError, participantError]);

  const handleCloseErrorMessage = () => {
    setShowErrorMessage(false);
  };

  useEffect(() => {
    if (isParticipantError) {
      toggleToast({
        open: true,
        message: commonLabels.someThingWentWrongPleaseTryAgainLater,
        type: "error",
      });
    }
  }, [isParticipantError]);

  useEffect(() => {
    dispatch(
      toggleLoader({
        isParticipantPending: {
          isVisible: isParticipantPending,
        },
      }),
    );
    return () => {
      dispatch(
        toggleLoader({
          isParticipantPending: {
            isVisible: false,
          },
        }),
      );
    };
  }, [isParticipantPending]);

  const methods = useForm({
    mode: "all",
    defaultValues: searchParticipantsDefaultValues,
    resolver: yupResolver(BulkActivitySearchParticipantSchema()),
  });

  const {
    handleSubmit,
    reset,
    getValues,
    formState: { isDirty },
  } = methods;

  const prepareData = () => {
    const {
      participantName,
      participantNameFilter,
      programName,
      programNameFilter,
      clientName,
      clientNameFilter,
      productLine,
      programTemplate,
      status,
      deliveryCountry,
      deliveryOffice,
      deliveryOfficeFilter,
      coach,
      coachFilter,
      createdDate,
    } = getValues();

    return {
      participantNameFilter,
      participantName: trimValue(participantName),
      programNameFilter,
      programName: trimValue(programName),
      clientFilter: clientNameFilter,
      client: trimValue(clientName),
      lob: isNotEmpty(productLine) ? [productLine] : [],
      programTemplateId: isNotEmpty(programTemplate) ? programTemplate : [],
      status,
      deliveryCountryCode: isNotEmpty(deliveryCountry) ? [deliveryCountry] : [],
      deliveryOfficeFilter,
      deliveryOffice: trimValue(deliveryOffice),
      coachFilter,
      coach: trimValue(coach),
      createdTimeStamp: isNotEmpty(createdDate)
        ? convertToUTCFormat(createdDate, userCountryDateFormat)
        : "",
    };
  };

  const getParticipantListApi = () => {
    getParticipantList(prepareData());
  };

  const handleResetConfirmation = () => {
    setShowResetConfirmationModal(false);
    if (isSearchClicked) {
      dispatch(setBulkActivitySelectedParticipants([]));
      dispatch(setBulkActivityList([]));
      getParticipantListApi();
    } else {
      handleReset();
    }
  };

  const handleCommonResetAndSearchClick = (
    fetchParticipants: boolean,
    resetForm: boolean,
    isFromAddButtonClick?: boolean,
  ) => {
    if (activityList.length > 0 && !isFromAddButtonClick) {
      setShowResetConfirmationModal(true);
    } else {
      if (resetForm) {
        handleReset();
      }
      if (fetchParticipants) {
        dispatch(setBulkActivitySelectedParticipants([]));
        getParticipantListApi();
      }
    }
  };

  const handleSearchClick = () => {
    setIsSearchClicked(true);
    handleCommonResetAndSearchClick(true, false);
    dispatch(setBulkActivityProgramTemplateId(getValues().programTemplate));
  };

  const renderHeader = (labelKey: string) => {
    return <AccordionHeaderText label={fetchComponentTitle(labelKey)} />;
  };

  const renderResultHeader = () => {
    return (
      <MuiBox className="result-header-container">
        {renderHeader(bulkActivitySiteCoreKeys.searchParticipantsResultsKey)}
        <MuiTypography variant="h3Bold" className="selected-count">
          {getValueOrZero(participantResultsList?.length)}
        </MuiTypography>
      </MuiBox>
    );
  };

  const handleResetButton = (isFromAddButtonClick: boolean) => {
    setIsSearchClicked(false);
    handleCommonResetAndSearchClick(false, true, isFromAddButtonClick);
  };

  useImperativeHandle(ref, () => ({
    handleResetButtonRef: () => handleResetButton(true),
  }));

  const handleCloseInformation = () => {
    setShowInformationModal(false);
  };

  const handleReset = () => {
    reset(searchParticipantsDefaultValues);
    programTemplateRef.current?.resetValues([]);
    dispatch(setBulkActivityProgramTemplateId([]));
    dispatch(setBulkActivitySelectedParticipants([]));
    dispatch(setBulkActivityList([]));
    setParticipantResultsList([]);
  };

  return (
    <BulkActivitySearchParticipantsWrapper>
      <MuiBox className="accordion-header" data-testid="search-participant">
        <FormProvider {...methods}>
          <AccordionHeader
            title={renderHeader(bulkActivitySiteCoreKeys.searchParticipantsKey)}
            onChange={() =>
              handleIndividualAccordionChange("searchParticipant")
            }
            isExpanded={accordionStatus.searchParticipant}
          >
            <MuiBox className="semi-header">
              <ButtonWithIcon
                data-testid={`${pageId}-clearAll`}
                buttonText={commonLabels.resetAllSearch}
                onActionClick={() => handleResetButton(false)}
                ActionIcon={ResetAllIcon}
              />
            </MuiBox>
            <Separator />
            <SearchParticipantsForm
              pageId={pageId}
              programTemplateRef={programTemplateRef}
            />
            <MuiBox className="button-container">
              <MuiButtonPrimary
                id={`${pageId}-search`}
                onClick={() => handleSubmit(handleSearchClick)()}
              >
                {commonLabels.search}
              </MuiButtonPrimary>
            </MuiBox>
          </AccordionHeader>
        </FormProvider>
      </MuiBox>
      <MuiBox className="accordion-header">
        <AccordionHeader
          title={renderResultHeader()}
          onChange={() => handleIndividualAccordionChange("searchResult")}
          isExpanded={accordionStatus.searchResult}
        >
          <SearchParticipantsResult
            rows={participantResultsList}
            pageId={pageId}
            setShowInformationModal={setShowInformationModal}
            currentStep={currentStep}
          />
        </AccordionHeader>
      </MuiBox>
      <BulkInsertFooter
        pageId={pageId}
        onConfirmClick={onNextButtonClick}
        handleNavigateBack={handleNavigateBack}
        isFiledUpdated={isDirty}
        confirmButtonText={commonLabels.next}
      />
      {showInformationModal && (
        <InformationModal
          showModal={showInformationModal}
          handleModalClose={handleCloseInformation}
          handlePrimaryButtonClick={handleCloseInformation}
          message={fetchLabel(
            bulkActivitySiteCoreKeys.searchParticipantsResultsKey,
            bulkActivitySiteCoreKeys.addParticipantsWarningMessage,
          )}
        />
      )}

      {showResetConfirmationModal && (
        <WarningConfirmationModal
          modalTitle={commonLabels.confirmation}
          labelContent={fetchLabel(
            bulkActivitySiteCoreKeys.searchParticipantsKey,
            bulkActivitySiteCoreKeys.resetAllSearchWarningMessage,
          )}
          show={showResetConfirmationModal}
          onClose={() => setShowResetConfirmationModal(false)}
          onPrimaryButtonClick={handleResetConfirmation}
          primaryButtonTitle={commonLabels.yes}
          secondaryButtonTitle={commonLabels.no}
        />
      )}

      {showErrorMessage && (
        <InformationModal
          showModal={showErrorMessage}
          handleModalClose={handleCloseErrorMessage}
          handlePrimaryButtonClick={handleCloseErrorMessage}
          message={commonLabels.refineYourSearchInformationMessage}
        />
      )}
    </BulkActivitySearchParticipantsWrapper>
  );
});
